import { ChangeEvent, FormEvent, useCallback, useEffect, useState } from "react";
import { MapPinned, Plus, Save } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { httpServices as services } from "@/services";
import type { FarmDetail } from "@/types/domain";
import { PageHeader } from "@/components/PageHeader";
import { ErrorState, LoadingState } from "@/components/shared/StatePanels";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function FarmFieldUpdatePage() {
  const { farmId = "" } = useParams();
  const navigate = useNavigate();
  const [farm, setFarm] = useState<FarmDetail>();
  const [error, setError] = useState("");
  const [farmName, setFarmName] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [plotName, setPlotName] = useState("");
  const [plotArea, setPlotArea] = useState("");
  const [plotOwnership, setPlotOwnership] = useState("");
  const [plotLandUse, setPlotLandUse] = useState("");
  const [creatingPlot, setCreatingPlot] = useState(false);

  const load = useCallback(async () => {
    try {
      const record = await services.farms.get(farmId);
      if (!record) throw new Error("Farm not found");
      setFarm(record);
      setFarmName(record.name);
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Farm could not be loaded.");
    }
  }, [farmId]);

  useEffect(() => { void load(); }, [load]);

  if (error && !farm) return <ErrorState message={error} />;
  if (!farm) return <LoadingState label="Loading Farm workspace" />;

  const saveFarmName = async () => {
    if (!farmName.trim()) { toast.error("Farm name is required."); return; }
    setSavingName(true);
    try {
      const updated = await services.farms.updateFarm(farm.id, { name: farmName.trim() } as Partial<FarmDetail>, farm.rowVersion || 1);
      setFarm(updated);
      toast.success("Farm name updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Farm name could not be updated.");
    } finally {
      setSavingName(false);
    }
  };

  const createPlot = async (event: FormEvent) => {
    event.preventDefault();
    const area = plotArea.trim() ? Number(plotArea) : undefined;
    if (area !== undefined && (!Number.isFinite(area) || area <= 0)) { toast.error("Plot acreage must be greater than zero."); return; }
    setCreatingPlot(true);
    try {
      const wasAdditionalPlot = farm.plotsData.length > 0;
      await services.farms.createPlot({
        farmId: farm.id,
        plotName: plotName.trim() || `Plot ${farm.plotsData.length + 1}`,
        reportedAreaAcres: area,
        ownershipType: plotOwnership || undefined,
        currentLandUse: plotLandUse.trim() || undefined,
      });
      setPlotName("");
      setPlotArea("");
      setPlotOwnership("");
      setPlotLandUse("");
      await load();
      toast.success("Plot added", {
        description: wasAdditionalPlot
          ? "A new signed-agreement requirement has been created for this additional Plot."
          : "The first Plot is now part of this Farm. Complete its GPS, crop and agronomy details from Manage Plot.",
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Plot could not be created.");
    } finally {
      setCreatingPlot(false);
    }
  };

  const derivedArea = farm.plotsData.reduce((sum, plot) => sum + (plot.reportedAreaAcres || 0), 0);
  const verifiedPlots = farm.plotsData.filter((plot) => plot.boundaryVerified).length;

  return <div className="space-y-6" data-testid="farm-field-update-page">
    <PageHeader
      eyebrow="Farms"
      title={`Manage ${farm.name}`}
      description="The Farm is the farmer-level umbrella. Acreage, ownership and GPS are maintained through its individual Plots."
      backTo={`/farms/${farm.id}`}
    />

    {error && <p className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive">{error}</p>}

    <Card>
      <CardHeader><CardTitle>Farm summary</CardTitle><CardDescription>Farm acreage is calculated from active Plot acreages and is not typed separately.</CardDescription></CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-[1fr_180px_180px]">
        <div className="space-y-2"><Label>Farm name</Label><Input value={farmName} onChange={(e: ChangeEvent<HTMLInputElement>) => setFarmName(e.target.value)} /></div>
        <div className="space-y-2"><Label>Total acreage</Label><Input value={derivedArea ? derivedArea.toFixed(2) : ""} readOnly placeholder="From Plots" /></div>
        <div className="flex items-end"><Button type="button" variant="outline" onClick={saveFarmName} disabled={savingName}><Save className="mr-2 size-4" />{savingName ? "Saving..." : "Save name"}</Button></div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader><CardTitle>Plots</CardTitle><CardDescription>Each Plot is an independent land unit. Ownership, acreage, GPS/boundary, crop establishment, plant population and expected yield are managed at Plot level.</CardDescription></CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-lg border p-4"><p className="text-xs text-muted-foreground">Active Plots</p><p className="mt-1 text-2xl font-semibold">{farm.plotsData.length}</p></div>
          <div className="rounded-lg border p-4"><p className="text-xs text-muted-foreground">Derived Farm acreage</p><p className="mt-1 text-2xl font-semibold">{derivedArea.toFixed(2)} ac</p></div>
          <div className="rounded-lg border p-4"><p className="text-xs text-muted-foreground">Verified Plot boundaries</p><p className="mt-1 text-2xl font-semibold">{verifiedPlots}/{farm.plotsData.length}</p></div>
        </div>

        {farm.plotsData.length > 0 && <div className="space-y-3">
          {farm.plotsData.map((plot, index) => <div key={plot.id} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border p-4">
            <div>
              <div className="flex items-center gap-2"><p className="font-semibold">Plot {index + 1}: {plot.name}</p><Badge variant="outline">{plot.status}</Badge></div>
              <p className="mt-1 text-sm text-muted-foreground">{plot.reportedAreaAcres ? `${plot.reportedAreaAcres} ac` : "Acreage pending"} · {plot.ownershipType || "Ownership pending"} · {plot.currentLandUse || "Land use pending"}</p>
              <p className="mt-1 text-xs text-muted-foreground">{plot.boundaryVerified ? "GPS boundary verified" : plot.boundaryCaptured ? "GPS boundary awaiting verification" : "GPS boundary not yet captured"}</p>
            </div>
            <Button asChild><Link to={`/farms/${farm.id}/plots/${plot.id}/map`}><MapPinned className="mr-2 size-4" />Manage Plot</Link></Button>
          </div>)}
        </div>}

        <form onSubmit={createPlot} className="rounded-xl border bg-muted/20 p-4">
          <p className="font-semibold">Add another Plot</p>
          <p className="mt-1 text-sm text-muted-foreground">Adding an additional Plot keeps the Farm total in sync and creates the corresponding agreement requirement.</p>
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2"><Label>Plot / block name</Label><Input value={plotName} onChange={(e: ChangeEvent<HTMLInputElement>) => setPlotName(e.target.value)} placeholder={`Optional — defaults to Plot ${farm.plotsData.length + 1}`} /></div>
            <div className="space-y-2"><Label>Plot acreage</Label><Input type="number" min="0" step="0.01" value={plotArea} onChange={(e: ChangeEvent<HTMLInputElement>) => setPlotArea(e.target.value)} /></div>
            <div className="space-y-2"><Label>Ownership / tenure</Label><Select value={plotOwnership} onValueChange={setPlotOwnership}><SelectTrigger><SelectValue placeholder="Select when known" /></SelectTrigger><SelectContent><SelectItem value="OWNED">Owned</SelectItem><SelectItem value="LEASED">Leased</SelectItem><SelectItem value="HIRED">Hired / rented</SelectItem><SelectItem value="FAMILY">Family / customary land</SelectItem><SelectItem value="OTHER">Other</SelectItem></SelectContent></Select></div>
            <div className="space-y-2"><Label>Current land use</Label><Input value={plotLandUse} onChange={(e: ChangeEvent<HTMLInputElement>) => setPlotLandUse(e.target.value)} placeholder="Coffee, maize, fallow..." /></div>
          </div>
          <Button type="submit" className="mt-4" disabled={creatingPlot}><Plus className="mr-2 size-4" />{creatingPlot ? "Adding..." : "Add Plot"}</Button>
        </form>
      </CardContent>
    </Card>

    <div className="flex justify-end"><Button variant="outline" onClick={() => navigate(`/farms/${farm.id}`)}>Back to Farm</Button></div>
  </div>;
}
